/**
 * Carousel Frontend JavaScript
 * Based on WAI-ARIA Carousel Pattern
 * https://www.w3.org/WAI/ARIA/apg/patterns/carousel/examples/carousel-1-prev-next/
 */

class NPICarousel {
	constructor(carouselElement) {
		this.carousel = carouselElement;
		this.slidesContainer = this.carousel.querySelector('.npi-carousel-slides');
		this.slides = [];
		this.currentSlide = 0;
		this.autoPlay = this.carousel.dataset.autoPlay === 'true';
		this.autoPlayInterval = parseInt(this.carousel.dataset.autoPlayInterval) || 5000;
		this.showControls = this.carousel.dataset.showControls === 'true';
		this.showIndicators = this.carousel.dataset.showIndicators === 'true';
		this.autoPlayTimer = null;
		this.isPlaying = false;
		
		this.init();
	}
	
	init() {
		this.setupSlides();
		this.createControls();
		this.createIndicators();
		this.setupEventListeners();
		this.showSlide(0);
		
		if (this.autoPlay) {
			this.startAutoPlay();
		}
	}
	
	setupSlides() {
		// Get all direct child blocks as slides
		const slideElements = this.slidesContainer.children;
		this.slides = Array.from(slideElements).map((slide, index) => {
			slide.classList.add('npi-carousel-slide');
			slide.setAttribute('role', 'group');
			slide.setAttribute('aria-roledescription', 'slide');
			slide.setAttribute('aria-label', `${index + 1} of ${slideElements.length}`);
			slide.setAttribute('data-slide-index', index);
			return slide;
		});
		
		// Hide all slides initially
		this.slides.forEach((slide, index) => {
			// slide.style.display = index === 0 ? 'block' : 'none';
			slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
		});
	}
	
	createControls() {
		if (!this.showControls || this.slides.length <= 1) return;
		
		const controlsContainer = document.createElement('div');
		controlsContainer.className = 'npi-carousel-controls';
		
		// Previous button
		this.prevButton = document.createElement('button');
		this.prevButton.className = 'npi-carousel-control npi-carousel-prev';
		this.prevButton.type = 'button';
		this.prevButton.setAttribute('aria-label', 'Previous slide');
		this.prevButton.innerHTML = '<span class="screen-reader-text">Previous</span>';
		
		// Next button
		this.nextButton = document.createElement('button');
		this.nextButton.className = 'npi-carousel-control npi-carousel-next';
		this.nextButton.type = 'button';
		this.nextButton.setAttribute('aria-label', 'Next slide');
		this.nextButton.innerHTML = '<span class="screen-reader-text">Next</span>';
		
		// Auto-play toggle (if auto-play is enabled)
		if (this.autoPlay) {
			this.playPauseButton = document.createElement('button');
			this.playPauseButton.className = 'npi-carousel-control npi-carousel-play-pause';
			this.playPauseButton.type = 'button';
			this.updatePlayPauseButton();
		}
		
		controlsContainer.appendChild(this.prevButton);
		controlsContainer.appendChild(this.nextButton);
		
		if (this.playPauseButton) {
			controlsContainer.appendChild(this.playPauseButton);
		}
		
		this.carousel.appendChild(controlsContainer);
	}
	
	createIndicators() {
		if (!this.showIndicators || this.slides.length <= 1) return;
		
		this.indicatorsContainer = document.createElement('div');
		this.indicatorsContainer.className = 'npi-carousel-indicators';
		this.indicatorsContainer.setAttribute('role', 'group');
		this.indicatorsContainer.setAttribute('aria-label', 'Slide indicators');
		
		this.indicators = this.slides.map((slide, index) => {
			const indicator = document.createElement('button');
			indicator.className = 'npi-carousel-indicator';
			indicator.type = 'button';
			indicator.setAttribute('aria-label', `Go to slide ${index + 1}`);
			indicator.setAttribute('data-slide-index', index);
			return indicator;
		});
		
		this.indicators.forEach(indicator => {
			this.indicatorsContainer.appendChild(indicator);
		});
		
		this.carousel.appendChild(this.indicatorsContainer);
	}
	
	setupEventListeners() {
		// Control buttons
		if (this.prevButton) {
			this.prevButton.addEventListener('click', () => this.previousSlide());
		}
		
		if (this.nextButton) {
			this.nextButton.addEventListener('click', () => this.nextSlide());
		}
		
		if (this.playPauseButton) {
			this.playPauseButton.addEventListener('click', () => this.toggleAutoPlay());
		}
		
		// Indicator buttons
		this.indicators?.forEach((indicator, index) => {
			indicator.addEventListener('click', () => this.goToSlide(index));
		});
		
		// Keyboard navigation
		this.carousel.addEventListener('keydown', (e) => this.handleKeydown(e));
		
		// Pause auto-play on hover/focus
		if (this.autoPlay) {
			this.carousel.addEventListener('mouseenter', () => this.pauseAutoPlay());
			this.carousel.addEventListener('mouseleave', () => this.resumeAutoPlay());
			this.carousel.addEventListener('focusin', () => this.pauseAutoPlay());
			this.carousel.addEventListener('focusout', () => this.resumeAutoPlay());
		}
	}
	
	showSlide(index) {
		if (index < 0 || index >= this.slides.length) return;
		
		// Remove all positional classes from all slides
		this.slides.forEach(slide => {
			slide.classList.remove('npi-carousel-slide--prev', 'npi-carousel-slide--current', 'npi-carousel-slide--next');
		});
		
		// Hide current slide
		if (this.slides[this.currentSlide]) {
			// this.slides[this.currentSlide].style.display = 'none';
			this.slides[this.currentSlide].setAttribute('aria-hidden', 'true');
		}
		
		// Show new slide
		this.currentSlide = index;
		this.slides[this.currentSlide].style.display = 'block';
		this.slides[this.currentSlide].setAttribute('aria-hidden', 'false');
		
		// Add positional classes
		const prevIndex = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
		const nextIndex = (this.currentSlide + 1) % this.slides.length;
		
		this.slides[prevIndex].classList.add('npi-carousel-slide--prev');
		this.slides[this.currentSlide].classList.add('npi-carousel-slide--current');
		this.slides[nextIndex].classList.add('npi-carousel-slide--next');
		
		// Update indicators
		this.updateIndicators();
		
		// Update controls
		this.updateControls();
		
		// Announce slide change to screen readers
		this.announceSlideChange();
	}
	
	nextSlide() {
		const nextIndex = (this.currentSlide + 1) % this.slides.length;
		this.showSlide(nextIndex);
	}
	
	previousSlide() {
		const prevIndex = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
		this.showSlide(prevIndex);
	}
	
	goToSlide(index) {
		this.showSlide(index);
	}
	
	updateIndicators() {
		this.indicators?.forEach((indicator, index) => {
			const isActive = index === this.currentSlide;
			indicator.classList.toggle('active', isActive);
			indicator.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});
	}
	
	updateControls() {
		if (!this.prevButton || !this.nextButton) return;
		
		// Enable/disable controls based on current slide (if not looping)
		// For now, we'll always loop, so controls are always enabled
		this.prevButton.disabled = false;
		this.nextButton.disabled = false;
	}
	
	handleKeydown(event) {
		const { key } = event;
		
		switch (key) {
			case 'ArrowLeft':
				event.preventDefault();
				this.previousSlide();
				break;
				
			case 'ArrowRight':
				event.preventDefault();
				this.nextSlide();
				break;
				
			case 'Home':
				event.preventDefault();
				this.goToSlide(0);
				break;
				
			case 'End':
				event.preventDefault();
				this.goToSlide(this.slides.length - 1);
				break;
		}
	}
	
	startAutoPlay() {
		if (!this.autoPlay) return;
		
		this.isPlaying = true;
		this.autoPlayTimer = setInterval(() => {
			this.nextSlide();
		}, this.autoPlayInterval);
		
		this.updatePlayPauseButton();
	}
	
	stopAutoPlay() {
		if (this.autoPlayTimer) {
			clearInterval(this.autoPlayTimer);
			this.autoPlayTimer = null;
		}
		
		this.isPlaying = false;
		this.updatePlayPauseButton();
	}
	
	toggleAutoPlay() {
		if (this.isPlaying) {
			this.stopAutoPlay();
		} else {
			this.startAutoPlay();
		}
	}
	
	pauseAutoPlay() {
		if (this.autoPlayTimer) {
			clearInterval(this.autoPlayTimer);
			this.autoPlayTimer = null;
		}
	}
	
	resumeAutoPlay() {
		if (this.autoPlay && this.isPlaying && !this.autoPlayTimer) {
			this.autoPlayTimer = setInterval(() => {
				this.nextSlide();
			}, this.autoPlayInterval);
		}
	}
	
	updatePlayPauseButton() {
		if (!this.playPauseButton) return;
		
		const isPlaying = this.isPlaying;
		this.playPauseButton.setAttribute('aria-label', isPlaying ? 'Pause auto-play' : 'Start auto-play');
		this.playPauseButton.innerHTML = isPlaying 
			? '<span class="screen-reader-text">Pause</span><span aria-hidden="true">⏸</span>'
			: '<span class="screen-reader-text">Play</span><span aria-hidden="true">▶</span>';
	}
	
	announceSlideChange() {
		// Create a live region for screen reader announcements
		if (!this.liveRegion) {
			this.liveRegion = document.createElement('div');
			this.liveRegion.setAttribute('aria-live', 'polite');
			this.liveRegion.setAttribute('aria-atomic', 'true');
			this.liveRegion.className = 'screen-reader-text';
			this.carousel.appendChild(this.liveRegion);
		}
		
		const announcement = `Slide ${this.currentSlide + 1} of ${this.slides.length}`;
		this.liveRegion.textContent = announcement;
	}
}

// Initialize carousels when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
	const carousels = document.querySelectorAll('.npi-carousel');
	carousels.forEach(carousel => new NPICarousel(carousel));
});

// Re-initialize for dynamically added carousels (for block editor)
if (window.wp && window.wp.domReady) {
	wp.domReady(() => {
		const carousels = document.querySelectorAll('.npi-carousel');
		carousels.forEach(carousel => {
			if (!carousel.dataset.initialized) {
				new NPICarousel(carousel);
				carousel.dataset.initialized = 'true';
			}
		});
	});
}